import { propertyFilters, propertySortOptions } from '../../../data/siteContent';
import PropertyCard from './PropertyCard';

function HomePropertyOverviewSection({
  activeFilter,
  activeType,
  availableTypes,
  canBrowseProperties,
  currentPage,
  filteredProperties,
  goToNextPage,
  goToPage,
  goToPreviousPage,
  handleFilterChange,
  handleSearchChange,
  handleSortChange,
  handleTypeChange,
  isLoading,
  portfolioReturnUrl,
  resetFilters,
  searchTerm,
  sortKey,
  totalPages,
  visibleProperties,
}) {
  const hasActiveFilters =
    Boolean(searchTerm) || activeType !== 'alle' || sortKey !== 'standard' || activeFilter !== 'alle';

  return (
    <section className="bg-cream-100 py-20 lg:py-28" id="eiendommer">
      <div className="site-rail">
        <div className="mb-10">
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-sand-600">
            <span className="h-px w-8 bg-sand-500" />
            Eiendomsoversikt
          </p>
          <h2 className="mt-5 font-serif text-[clamp(2.2rem,4.6vw,3.6rem)] font-bold leading-[1.04] text-forest-900 text-balance">
            Ledige boliger og bygg til leie.
          </h2>
        </div>

        <div className="mb-10 grid gap-5 rounded-[1.75rem] border border-forest-700/10 bg-white p-6 shadow-soft lg:p-7">
          <div className="grid gap-4 md:grid-cols-3">
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-sand-600">Sok</span>
              <input
                className="focus-ring rounded-full border border-forest-700/15 bg-cream-100 px-5 py-3 text-sm text-forest-900 transition focus:border-forest-700/40"
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Sted eller navn"
                type="search"
                value={searchTerm}
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-sand-600">Type</span>
              <select
                className="focus-ring rounded-full border border-forest-700/15 bg-cream-100 px-5 py-3 text-sm text-forest-900 transition focus:border-forest-700/40"
                onChange={(event) => handleTypeChange(event.target.value)}
                value={activeType}
              >
                <option value="alle">Alle typer</option>
                {availableTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-sand-600">Sorter</span>
              <select
                className="focus-ring rounded-full border border-forest-700/15 bg-cream-100 px-5 py-3 text-sm text-forest-900 transition focus:border-forest-700/40"
                onChange={(event) => handleSortChange(event.target.value)}
                value={sortKey}
              >
                {propertySortOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-col gap-3 border-t border-forest-700/10 pt-5 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2.5">
              {propertyFilters.map((filter) => {
                const isActive = filter.key === activeFilter;

                return (
                  <button
                    className={
                      isActive
                        ? 'focus-ring rounded-full bg-forest-700 px-5 py-2.5 text-sm font-semibold text-cream-100 shadow-md transition'
                        : 'focus-ring rounded-full border border-forest-700/15 bg-cream-100 px-5 py-2.5 text-sm font-semibold text-forest-500 transition hover:border-forest-700/30 hover:text-forest-900'
                    }
                    key={filter.key}
                    onClick={() => handleFilterChange(filter.key)}
                    type="button"
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-forest-500">
              <span aria-live="polite" role="status">
                Viser <strong className="text-forest-900">{visibleProperties.length}</strong> av{' '}
                {filteredProperties.length} boliger
              </span>
              {hasActiveFilters ? (
                <button
                  className="focus-ring rounded-full border border-forest-700/15 bg-white px-4 py-2 text-sm font-semibold text-forest-500 transition hover:text-forest-900"
                  onClick={resetFilters}
                  type="button"
                >
                  Nullstill
                </button>
              ) : null}
            </div>
          </div>
        </div>
        <div className="relative">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
            {(isLoading ? [] : visibleProperties).map((property) => (
              <PropertyCard key={property.slug} portfolioReturnUrl={portfolioReturnUrl} property={property} />
            ))}
          </div>
          {canBrowseProperties ? (
            <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
              <button
                aria-label="Gå til forrige side"
                className="focus-ring rounded-full border border-forest-700/15 bg-white px-5 py-2.5 text-sm font-semibold text-forest-500 transition hover:text-forest-900 disabled:cursor-default disabled:opacity-40"
                disabled={currentPage <= 0}
                onClick={goToPreviousPage}
                type="button"
              >
                Forrige
              </button>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  aria-current={index === currentPage ? 'page' : undefined}
                  className={
                    index === currentPage
                      ? 'focus-ring rounded-full bg-forest-700 px-5 py-2.5 text-sm font-semibold text-cream-100 shadow-md'
                      : 'focus-ring rounded-full border border-forest-700/15 bg-white px-5 py-2.5 text-sm font-semibold text-forest-500 transition hover:text-forest-900'
                  }
                  key={`page-${index}`}
                  onClick={() => goToPage(index)}
                  type="button"
                >
                  {index + 1}
                </button>
              ))}
              <button
                aria-label="Gå til neste side"
                className="focus-ring rounded-full border border-forest-700/15 bg-white px-5 py-2.5 text-sm font-semibold text-forest-500 transition hover:text-forest-900 disabled:cursor-default disabled:opacity-40"
                disabled={currentPage >= totalPages - 1}
                onClick={goToNextPage}
                type="button"
              >
                Neste
              </button>
            </div>
          ) : null}
          {!isLoading && visibleProperties.length === 0 ? (
            <div className="rounded-[1.75rem] border border-forest-700/10 bg-white px-7 py-10 text-center text-forest-500 shadow-soft" role="status">
              <p className="m-0 text-[1.05rem] leading-[1.7]">Ingen boliger matcher søket eller filtrene akkurat nå.</p>
              {hasActiveFilters ? (
                <button
                  className="focus-ring mt-5 rounded-full bg-forest-700 px-6 py-2.5 text-sm font-semibold text-cream-100"
                  onClick={resetFilters}
                  type="button"
                >
                  Nullstill filtre
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default HomePropertyOverviewSection;